import { type SupportedCountryCode } from '@zep-us/i18n';
import { type GetServerSidePropsContext } from 'next/types';

import {
  CookieManager,
  type CookieOptions,
  type ServerContext,
} from './cookieManager';

/**
 * 사용자 관련 쿠키 매니저
 * CookieManager 인스턴스를 composition으로 사용
 */
class UserCookieManagerClass {
  private readonly userCountryCodeManager: CookieManager;
  private readonly sessionManager: CookieManager;
  private readonly countryCodeManager: CookieManager;

  constructor() {
    this.userCountryCodeManager = new CookieManager('userCountryCode');
    this.sessionManager = new CookieManager('session-quiz-zep');
    this.countryCodeManager = new CookieManager('countryCode');
  }

  /**
   * 유저 국가 코드 쿠키 설정 (로그인 시 사용)
   */
  setUserCountryCode(
    countryCode: string,
    expiredAt: string,
    context?: ServerContext,
  ): void {
    const options: CookieOptions = {
      expires: new Date(expiredAt),
    };
    // CookieManager를 직접 수정하지 않고 새 인스턴스 생성
    const manager = new CookieManager('userCountryCode', options);
    manager.set(countryCode, context);
  }

  /**
   * 유저 국가 코드 쿠키 조회
   */
  getUserCountryCode(
    context?: ServerContext,
  ): SupportedCountryCode | string | undefined {
    return this.userCountryCodeManager.get(context);
  }

  /**
   * 유저 국가 코드 쿠키 삭제 (로그아웃 시 사용)
   */
  clearUserCountryCode(context?: ServerContext): void {
    this.userCountryCodeManager.delete(context);
  }

  /**
   * 세션 쿠키 설정
   */
  setSession(context?: ServerContext): void {
    this.sessionManager.set('true', context);
  }

  /**
   * 세션 쿠키 삭제
   */
  clearSession(context?: ServerContext): void {
    this.sessionManager.delete(context);
  }

  /**
   * 모든 유저 관련 쿠키 삭제 (로그아웃 시 사용)
   */
  clearAllUserCookies(context?: ServerContext): void {
    this.clearUserCountryCode(context);
    this.clearSession(context);
  }

  /**
   * 국가 코드에서 우선순위에 따른 값 반환
   * 1. 로그인한 유저의 국가 코드 (userCountryCode cookie)
   * 2. 지리적 위치 기반 국가 코드 (countryCode cookie)
   * 3. Middleware에서 전달한 국가 코드 (x-country-code header)
   */
  getPreferredCountryCode(
    context: GetServerSidePropsContext['req'],
  ): string | undefined {
    // Priority 1: 로그인한 유저의 국가 코드
    const userCountryCode = this.getUserCountryCode(context);
    if (userCountryCode) {
      return userCountryCode;
    }

    // Priority 2: 쿠키에 저장된 국가 코드
    const cookieCountryCode = this.countryCodeManager.get(context);
    if (cookieCountryCode) {
      return cookieCountryCode;
    }

    // Priority 3: Middleware에서 전달한 국가 코드 (첫 방문)
    if (context?.headers != null) {
      let countryCode = context.headers['x-country-code'];

      // IncomingHttpHeaders는 string | string[] 타입일 수 있음
      if (Array.isArray(countryCode)) {
        countryCode = countryCode[0];
      }

      if (countryCode && typeof countryCode === 'string') {
        return countryCode;
      }
    }

    return undefined;
  }
}

// Singleton instance
export const UserCookieManager = new UserCookieManagerClass();
